import { useEffect, useRef, useState, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

interface MatrixEffectProps {
  active: boolean;
  onComplete?: () => void;
}

// Phrases with timing - generous durations for comfortable reading
const PHRASES = [
  { text: 'VAS POR BUEN CAMINO', duration: 3200 },
  { text: 'EL HACKING NACE DE LA CURIOSIDAD', duration: 3200 },
  { text: 'ES UNA FORMA DE VER EL MUNDO', duration: 3200 },
  { text: 'LOWKEYX', duration: 3500 },
];

// Scramble characters for decode effect
const SCRAMBLE_CHARS = 'アイウエオカキクケコサシスセソ@#$%&*<>[]{}|01';

export const MatrixEffect = ({ active, onComplete }: MatrixEffectProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const phaseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const allowExitTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scrambleIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [phase, setPhase] = useState<'entering' | 'active' | 'exiting' | 'hidden'>('hidden');
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [phraseVisible, setPhraseVisible] = useState(false);
  const [glitchActive, setGlitchActive] = useState(false);
  const [canExit, setCanExit] = useState(false);
  const [scrambledText, setScrambledText] = useState('');
  const [revealedCount, setRevealedCount] = useState(0);

  // Cleanup all timeouts
  const clearAllTimeouts = useCallback(() => {
    if (phaseTimeoutRef.current) clearTimeout(phaseTimeoutRef.current);
    if (allowExitTimeoutRef.current) clearTimeout(allowExitTimeoutRef.current);
    if (scrambleIntervalRef.current) clearInterval(scrambleIntervalRef.current);
  }, []);

  const handleComplete = useCallback(() => {
    clearAllTimeouts();
    setCanExit(false);
    setPhase('hidden');
    setCurrentPhraseIndex(0);
    setPhraseVisible(false);
    setGlitchActive(false);
    setScrambledText('');
    setRevealedCount(0);
    cancelAnimationFrame(animationRef.current);
    onComplete?.();
  }, [onComplete, clearAllTimeouts]);

  // Handle ESC key and click
  useEffect(() => {
    if (!active) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleComplete();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [active, handleComplete]);

  // Phase management - start animation
  useEffect(() => {
    if (!active) {
      clearAllTimeouts();
      setCanExit(false);
      setPhase('hidden');
      return;
    }

    setCanExit(false);
    setPhase('entering');
    setCurrentPhraseIndex(0);
    setPhraseVisible(false);

    // Prevent immediate click-to-exit from the triggering interaction
    allowExitTimeoutRef.current = setTimeout(() => {
      setCanExit(true);
    }, 900);

    phaseTimeoutRef.current = setTimeout(() => {
      setPhase('active');
      setPhraseVisible(true);
    }, 800);

    return () => clearAllTimeouts();
  }, [active, clearAllTimeouts]);

  // Phrase progression - deterministic timeline (no shared timeout refs)
  useEffect(() => {
    if (phase !== 'active') return;

    const currentPhrase = PHRASES[currentPhraseIndex];
    const isLast = currentPhraseIndex === PHRASES.length - 1;

    // Finished all phrases -> exit sequence
    if (!currentPhrase) {
      setPhraseVisible(false);
      setGlitchActive(true);

      const exitGlitchTimeout = setTimeout(() => {
        setGlitchActive(false);
        setPhase('exiting');
      }, 600);

      const exitCompleteTimeout = setTimeout(() => {
        handleComplete();
      }, 2000);

      return () => {
        clearTimeout(exitGlitchTimeout);
        clearTimeout(exitCompleteTimeout);
      };
    }

    // Ensure phrase is visible while timer runs
    setPhraseVisible(true);

    // Reset scramble state for last phrase
    if (isLast) {
      setRevealedCount(0);
      setScrambledText(currentPhrase.text);
    }

    // Small glitch hit at start
    setGlitchActive(true);
    const glitchOffTimeout = setTimeout(() => setGlitchActive(false), 250);

    const cleanupFns: Array<() => void> = [];

    // After phrase duration, transition to next
    const nextPhraseTimeout = setTimeout(() => {
      setPhraseVisible(false);
      setGlitchActive(true);

      const transitionTimeout = setTimeout(() => {
        setGlitchActive(false);
        setCurrentPhraseIndex((prev) => prev + 1);
      }, 420);

      // ensure transition timeout is cleared if effect is torn down mid-flight
      cleanupFns.push(() => clearTimeout(transitionTimeout));
    }, currentPhrase.duration);

    return () => {
      clearTimeout(glitchOffTimeout);
      clearTimeout(nextPhraseTimeout);
      cleanupFns.forEach((fn) => fn());
    };
  }, [phase, currentPhraseIndex, handleComplete]);

  // Scramble effect for LOWKEYX
  useEffect(() => {
    const isLast = currentPhraseIndex === PHRASES.length - 1;
    if (!isLast || phase !== 'active' || !phraseVisible) return;

    const targetText = 'LOWKEYX';
    const scrambleSpeed = 40; // ms between scrambles
    const revealDelay = 120; // ms between each letter reveal
    let currentRevealed = 0;

    // Initial scramble
    const generateScrambled = (revealed: number) => {
      let result = '';
      for (let i = 0; i < targetText.length; i++) {
        if (i < revealed) {
          result += targetText[i];
        } else {
          result += SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
        }
      }
      return result;
    };

    // Fast scramble animation
    scrambleIntervalRef.current = setInterval(() => {
      setScrambledText(generateScrambled(currentRevealed));
    }, scrambleSpeed);

    // Reveal letters one by one
    const revealLetters = () => {
      if (currentRevealed < targetText.length) {
        currentRevealed++;
        setRevealedCount(currentRevealed);
        
        if (currentRevealed < targetText.length) {
          setTimeout(revealLetters, revealDelay);
        } else {
          // All revealed - stop scrambling
          if (scrambleIntervalRef.current) {
            clearInterval(scrambleIntervalRef.current);
          }
          setScrambledText(targetText);
        }
      }
    };

    // Start revealing after a short delay
    const startRevealTimeout = setTimeout(revealLetters, 400);

    return () => {
      if (scrambleIntervalRef.current) {
        clearInterval(scrambleIntervalRef.current);
      }
      clearTimeout(startRevealTimeout);
    };
  }, [currentPhraseIndex, phase, phraseVisible]);

  // Matrix rain canvas effect - RED THEME with continuous cascade
  useEffect(() => {
    if (!active || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = window.innerWidth;
    let height = window.innerHeight;

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };
    resize();
    window.addEventListener('resize', resize);

    // Matrix setup - RED colors
    const chars = 'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ@#$%^&*<>[]{}|';
    const charArray = chars.split('');
    
    const fontSize = width < 640 ? 12 : 16;
    const columns = Math.ceil(width / fontSize);
    
    // Each column has its own properties
    const drops: number[] = [];
    const speeds: number[] = [];
    const lengths: number[] = [];
    
    for (let i = 0; i < columns; i++) {
      drops[i] = Math.random() * -50;
      speeds[i] = 0.3 + Math.random() * 1.2;
      lengths[i] = 8 + Math.floor(Math.random() * 18);
    }

    let lastTime = 0;
    const fps = 30;
    const frameInterval = 1000 / fps;

    const draw = (currentTime: number) => {
      animationRef.current = requestAnimationFrame(draw);
      
      const deltaTime = currentTime - lastTime;
      if (deltaTime < frameInterval) return;
      lastTime = currentTime - (deltaTime % frameInterval);

      // Fade effect - slightly faster for cleaner trails
      ctx.fillStyle = 'rgba(0, 0, 0, 0.06)';
      ctx.fillRect(0, 0, width, height);

      ctx.font = `${fontSize}px 'Courier New', monospace`;

      for (let i = 0; i < columns; i++) {
        const x = i * fontSize;
        const y = drops[i] * fontSize;

        // Draw trail
        for (let j = 0; j < lengths[i]; j++) {
          const trailY = y - j * fontSize;
          if (trailY < 0 || trailY > height) continue;

          const char = charArray[Math.floor(Math.random() * charArray.length)];
          
          if (j === 0) {
            // Leading character - bright white/pink with glow
            ctx.shadowBlur = 15;
            ctx.shadowColor = 'hsl(0, 100%, 70%)';
            ctx.fillStyle = '#ffffff';
          } else {
            ctx.shadowBlur = 0;
            const alpha = 1 - (j / lengths[i]);
            const lightness = 55 - (j / lengths[i]) * 35;
            // RED hue (0)
            ctx.fillStyle = `hsla(0, 90%, ${lightness}%, ${alpha})`;
          }
          
          ctx.fillText(char, x, trailY);
        }

        ctx.shadowBlur = 0;

        // Move drop
        drops[i] += speeds[i];

        // Reset when off screen - staggered reset for continuous effect
        if (drops[i] * fontSize > height + lengths[i] * fontSize) {
          drops[i] = Math.random() * -30;
          speeds[i] = 0.3 + Math.random() * 1.2;
          lengths[i] = 8 + Math.floor(Math.random() * 18);
        }
      }

      // Random flash effect - RED
      if (Math.random() > 0.995) {
        ctx.fillStyle = 'rgba(255, 50, 50, 0.03)';
        ctx.fillRect(0, 0, width, height);
      }
    };

    animationRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener('resize', resize);
    };
  }, [active]);

if (!active && phase === 'hidden') return null;

  const currentPhrase = PHRASES[currentPhraseIndex];
  const isLastPhrase = currentPhraseIndex === PHRASES.length - 1;

  return (
    <motion.div 
      className="fixed inset-0 z-[100] overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: phase === 'exiting' ? 0 : 1 }}
      transition={{ duration: phase === 'entering' ? 0.6 : 1.5, ease: 'easeInOut' }}
      onClick={() => {
        if (canExit) handleComplete();
      }}
    >
      {/* Canvas */}
      <canvas 
        ref={canvasRef} 
        className="absolute inset-0 w-full h-full"
        style={{ background: '#000000' }}
      />

      {/* Scanlines - subtle */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 0, 0, 0.4) 2px, rgba(0, 0, 0, 0.4) 4px)',
          animation: 'scanline 10s linear infinite',
        }}
      />

      {/* Vignette - RED tinted */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 0%, rgba(20,0,0,0.6) 70%, rgba(0,0,0,0.9) 100%)',
        }}
      />

      {/* Glitch overlay - RED */}
      <AnimatePresence>
        {glitchActive && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.08 }}
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,50,50,0.15) 50%, transparent 100%)',
              animation: 'glitchSlide 0.08s linear infinite',
            }}
          />
        )}
      </AnimatePresence>

      {/* Central text display */}
      <div className="absolute inset-0 flex items-center justify-center px-4 sm:px-8">
        <AnimatePresence mode="wait">
          {phraseVisible && currentPhrase && (
            <motion.div
              key={currentPhraseIndex}
              initial={{ opacity: 0, scale: 0.85, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 1.08, y: -25 }}
              transition={{ 
                duration: 0.5,
                ease: [0.16, 1, 0.3, 1]
              }}
              className="relative text-center"
            >
              {/* Glitch text layers - RED theme */}
              <div className="relative">
                {/* Red offset layer - darker */}
                <motion.h1 
                  className={cn(
                    "absolute inset-0 font-mono font-black tracking-tight",
                    isLastPhrase 
                      ? "text-[clamp(2.2rem,10vw,6rem)] tracking-[0.12em]" 
                      : "text-[clamp(1.4rem,7vw,4.5rem)]"
                  )}
                  style={{ color: 'hsl(0, 100%, 40%)' }}
                  animate={glitchActive ? {
                    x: [0, -6, 6, -4, 4, 0],
                    opacity: [0.6, 0.8, 0.4, 0.7, 0.5, 0.6],
                  } : {}}
                  transition={{ duration: 0.12, repeat: glitchActive ? Infinity : 0 }}
                >
                  {isLastPhrase ? scrambledText : currentPhrase.text}
                </motion.h1>
                
                {/* Orange offset layer */}
                <motion.h1 
                  className={cn(
                    "absolute inset-0 font-mono font-black tracking-tight",
                    isLastPhrase 
                      ? "text-[clamp(2.2rem,10vw,6rem)] tracking-[0.12em]" 
                      : "text-[clamp(1.4rem,7vw,4.5rem)]"
                  )}
                  style={{ color: 'hsl(20, 100%, 55%)' }}
                  animate={glitchActive ? {
                    x: [0, 6, -6, 4, -4, 0],
                    opacity: [0.5, 0.4, 0.7, 0.5, 0.6, 0.5],
                  } : {}}
                  transition={{ duration: 0.12, repeat: glitchActive ? Infinity : 0 }}
                >
                  {isLastPhrase ? scrambledText : currentPhrase.text}
                </motion.h1>
                
                {/* Main text - WHITE with RED X for LOWKEYX */}
                <motion.h1 
                  className={cn(
                    "relative font-mono font-black tracking-tight",
                    isLastPhrase 
                      ? "text-[clamp(2.2rem,10vw,6rem)] tracking-[0.12em]" 
                      : "text-[clamp(1.4rem,7vw,4.5rem)]"
                  )}
                  animate={glitchActive ? {
                    x: [0, 2, -2, 1, -1, 0],
                  } : {}}
                  transition={{ duration: 0.08, repeat: glitchActive ? Infinity : 0 }}
                >
                  {isLastPhrase ? (
                    // Render LOWKEYX with red X
                    <span>
                      {scrambledText.split('').map((char, index) => {
                        const isRevealed = index < revealedCount;
                        const isX = index === 6 && isRevealed; // Last character is X
                        
                        return (
                          <motion.span
                            key={index}
                            className={cn(
                              isX ? "text-red-500" : "text-white",
                              !isRevealed && "opacity-80"
                            )}
                            style={{
                              textShadow: isX
                                ? `
                                  0 0 20px hsla(0, 100%, 50%, 1),
                                  0 0 40px hsla(0, 100%, 50%, 0.8),
                                  0 0 60px hsla(0, 100%, 50%, 0.6),
                                  0 0 80px hsla(0, 100%, 50%, 0.4)
                                `
                                : `
                                  0 0 10px hsla(0, 100%, 60%, 0.9),
                                  0 0 25px hsla(0, 100%, 55%, 0.7),
                                  0 0 50px hsla(0, 100%, 50%, 0.5),
                                  0 0 100px hsla(0, 100%, 45%, 0.3)
                                `,
                            }}
                            initial={isRevealed && index === revealedCount - 1 ? { scale: 1.3, opacity: 0 } : {}}
                            animate={isRevealed && index === revealedCount - 1 ? { scale: 1, opacity: 1 } : {}}
                            transition={{ duration: 0.15, ease: 'easeOut' }}
                          >
                            {char}
                          </motion.span>
                        );
                      })}
                    </span>
                  ) : (
                    <span 
                      className="text-white"
                      style={{
                        textShadow: `
                          0 0 10px hsla(0, 100%, 60%, 0.9),
                          0 0 25px hsla(0, 100%, 55%, 0.7),
                          0 0 50px hsla(0, 100%, 50%, 0.5),
                          0 0 100px hsla(0, 100%, 45%, 0.3)
                        `,
                      }}
                    >
                      {currentPhrase.text}
                    </span>
                  )}
                </motion.h1>
              </div>

              {/* Animated underline - RED */}
              <motion.div 
                className="mx-auto mt-5 sm:mt-8 h-0.5 sm:h-1 rounded-full overflow-hidden"
                initial={{ width: 0 }}
                animate={{ width: '70%' }}
                transition={{ delay: 0.25, duration: 0.7, ease: 'easeOut' }}
              >
                <motion.div
                  className="h-full w-full"
                  style={{
                    background: 'linear-gradient(90deg, transparent, hsl(0, 100%, 55%), transparent)',
                  }}
                  animate={{ x: ['-100%', '100%'] }}
                  transition={{ duration: 1.8, repeat: Infinity, ease: 'linear' }}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Corner decorations - RED */}
      <motion.div 
        className="absolute top-4 left-4 font-mono text-[10px] sm:text-xs"
        style={{ color: 'hsl(0, 100%, 55%)' }}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 0.9, x: 0 }}
        transition={{ delay: 0.4 }}
      >
        <div className="flex items-center gap-2">
          <motion.span 
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: 'hsl(0, 100%, 55%)' }}
            animate={{ opacity: [1, 0.3, 1] }}
            transition={{ duration: 1, repeat: Infinity }}
          />
          <span className="hidden sm:inline">LOWKEYX SYSTEM</span>
          <span className="sm:hidden">LOWKEYX</span>
        </div>
      </motion.div>
      
      <motion.div 
        className="absolute top-4 right-4 font-mono text-[10px] sm:text-xs text-right"
        style={{ color: 'hsla(0, 100%, 55%, 0.7)' }}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 }}
      >
        <div className="hidden sm:block">ESC / TAP TO EXIT</div>
        <div className="sm:hidden">TAP TO EXIT</div>
      </motion.div>

      {/* Bottom corner */}
      <motion.div 
        className="absolute bottom-4 left-4 font-mono text-[10px]"
        style={{ color: 'hsla(0, 100%, 55%, 0.5)' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        <div className="hidden sm:block">MATRIX MODE v2.0</div>
      </motion.div>
    </motion.div>
  );
};

export default MatrixEffect;
